import React from "react";
import {
  FaTachometerAlt,
  FaClipboardList,
  FaTools,
  FaTicketAlt,
  FaUsers,
  FaBed,
  FaChartBar,
  FaUserShield,
  FaCalendarCheck,
  FaTasks,
  FaDesktop,
  FaArrowRight,
  FaRegSmile,
  FaRegClock,
  FaRegCheckCircle,
  FaRegChartBar,
} from "react-icons/fa";
import { motion } from "framer-motion";
import ImageGallery from "react-image-gallery";
import "react-image-gallery/styles/css/image-gallery.css";
import GallerySection from "../components/GallerySection";
import videoFile from "../assets/Gallery/Web/video1.mp4";

const features = [
  {
    title: "Dashboard & Real-Time Stats",
    description:
      "Single view provides Administrator with performance metrics most important to manage their facility. Monitor, total beds, employee performance, urgent ticket status, task completion stats at a glance, maintenance scheduling and performance, and critical real time communications.",
    icon: <FaTachometerAlt className="text-blue-600 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/ui.png"),
  },
  {
    title: "Template Management",
    description:
      "Assign our premade templates or create your own templates. Manage, search and filter active and past templates with ease and assign them to rooms or staff on a schedule.",
    icon: <FaClipboardList className="text-blue-500 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/template.png"),
  },
  {
    title: "Maintenance Module",
    description:
      "Log, assign, and track maintenance issues from a dedicated dashboard. Ensure nothing falls through the cracks.",
    icon: <FaTools className="text-blue-700 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/maintenance.png"),
  },
  {
    title: "Ticketing System",
    description:
      "Handle urgent and regular tickets efficiently. Track, prioritize, and resolve issues quickly.",
    icon: <FaTicketAlt className="text-yellow-500 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/ticket.png"),
  },
  {
    title: "Employee & Department Management",
    description:
      "Manage employees, departments, and roles. Assign tasks and monitor team performance.",
    icon: <FaUsers className="text-blue-400 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/emp.png"),
  },
  {
    title: "Room & Bed Management",
    description:
      "Easily manage rooms, beds, and assignments. Keep your facility organized and up to date.",
    icon: <FaBed className="text-blue-600 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/room.png"),
  },
  {
    title: "Reports & Analytics",
    description:
      "Generate detailed reports and analytics for compliance, performance, and audits.",
    icon: <FaChartBar className="text-green-600 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/report.png"),
  },
  {
    title: "Role-Based Access",
    description:
      "Secure, role-based access for admins, managers, and employees. Protect sensitive data and workflows.",
    icon: <FaUserShield className="text-blue-800 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/emp.png"),
  },
  {
    title: "Scheduling & Automation",
    description:
      "Automate template assignments by date, day, or custom schedule. Save time and reduce manual work.",
    icon: <FaCalendarCheck className="text-blue-500 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/customtask.png"),
  },
  {
    title: "Custom Task Creation",
    description:
      "Select from a catalogue of available templates specifically designed for Skilled Nursing Facilities or customize for your specific facility needs.",
    icon: <FaTasks className="text-blue-400 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/task.png"),
  },
  {
    title: "Modern, Responsive UI",
    description:
      "Enjoy a clean, intuitive interface with sidebar navigation and mobile-friendly design.",
    icon: <FaDesktop className="text-blue-600 text-3xl mb-2" />,
    src: require("../assets/Gallery/Web/ui.png"),
  },
];

const fadeIn = {
  hidden: { opacity: 0, scale: 0.98, y: 40 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { duration: 0.7, type: "spring" as const },
  },
};

const workflowSteps = [
  // { icon: <FaUserShield className="text-blue-600 text-2xl mb-2" />, label: "Login" },
  {
    icon: <FaTachometerAlt className="text-blue-600 text-2xl mb-2" />,
    label: "Dashboard",
  },
  {
    icon: <FaClipboardList className="text-blue-500 text-2xl mb-2" />,
    label: "Assign Templates",
  },
  {
    icon: <FaTasks className="text-blue-400 text-2xl mb-2" />,
    label: "Track Progress",
  },
  {
    icon: <FaChartBar className="text-green-600 text-2xl mb-2" />,
    label: "Generate Reports",
  },
];

const benefits = [
  {
    icon: <FaRegClock className="text-blue-500 text-2xl mb-2" />,
    title: "Save Time",
    desc: "Automate repetitive tasks and streamline workflows.",
  },
  {
    icon: <FaRegCheckCircle className="text-green-600 text-2xl mb-2" />,
    title: "Stay Compliant",
    desc: "Effortlessly meet regulatory requirements and pass audits.",
  },
  {
    icon: <FaRegSmile className="text-yellow-500 text-2xl mb-2" />,
    title: "Empower Staff",
    desc: "Give your team the tools they need to succeed.",
  },
  {
    icon: <FaRegChartBar className="text-blue-700 text-2xl mb-2" />,
    title: "Gain Insights",
    desc: "Make data-driven decisions with real-time analytics.",
  },
];

const testimonials = [
  {
    quote:
      "RoomRounds has made compliance so much easier for our team. The dashboard is a game changer!",
    name: "Sarah L.",
    title: "Director of Nursing, CareWell SNF",
  },
  {
    quote:
      "Assigning and tracking tasks is now effortless. Our staff loves the intuitive interface.",
    name: "James P.",
    title: "Administrator, GreenFields Facility",
  },
  {
    quote:
      "The maintenance and ticketing modules keep us organized and survey-ready.",
    name: "Priya S.",
    title: "Charge Nurse, Oakwood SNF",
  },
];

const Features: React.FC = () => {
  const [galleryTab, setGalleryTab] = React.useState(0);
  const [mediaType, setMediaType] = React.useState<"screenshots" | "videos">(
    "screenshots"
  );
  const [selectedFeature, setSelectedFeature] = React.useState<number | null>(
    null
  );

  return (
    <section className="max-w-6xl mx-auto py-12 px-4">
      {/* Hero/Intro Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="mb-12 text-center"
      >
        <h1 className="text-4xl md:text-5xl font-extrabold mb-4 text-blue-800 drop-shadow">
          All-in-One Portal for Modern SNFs
        </h1>
        <p className="text-lg md:text-xl text-gray-700 max-w-2xl mx-auto">
          RoomRounds brings together everything you need to manage compliance,
          staff, tasks, maintenance and reporting in one secure, easy-to-use
          platform.
        </p>
      </motion.div>

      {/* How It Works / Workflow Section */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
        className="mb-16"
      >
        <h2 className="text-3xl font-bold mb-8 text-center text-blue-700">
          How It Works
        </h2>
        <div className="flex flex-col md:flex-row items-center justify-center gap-6">
          {workflowSteps.map((step, i) => (
            <React.Fragment key={step.label}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeIn}
                className="flex flex-col items-center"
              >
                {step.icon}
                <span className="font-semibold text-blue-800">
                  {step.label}
                </span>
              </motion.div>
              {i < workflowSteps.length - 1 && (
                <FaArrowRight className="text-blue-300 text-xl mx-2 my-2 md:my-0" />
              )}
            </React.Fragment>
          ))}
        </div>
      </motion.div>

      {/* Features Section */}
      <h2 className="text-3xl font-bold mb-8 text-center text-blue-700">
        Portal Features
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
        {features.map((feature, i) => (
          <motion.div
            key={feature.title}
            className="bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center text-center hover:shadow-2xl transition min-h-[220px] cursor-pointer"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeIn}
            onClick={() => setSelectedFeature(i)}
          >
            {feature.icon}
            <h3 className="text-lg font-bold mb-2 text-blue-800">
              {feature.title}
            </h3>
            <p className="text-gray-600">{feature.description}</p>
          </motion.div>
        ))}
      </div>

      {/* Benefits/Outcomes Section */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
        className="mb-16"
      >
        <h2 className="text-3xl font-bold mb-8 text-center text-blue-700">
          What You Get
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
          {benefits.map((b) => (
            <div
              key={b.title}
              className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center"
            >
              {b.icon}
              <h3 className="text-lg font-bold mb-2 text-blue-800">
                {b.title}
              </h3>
              <p className="text-gray-600">{b.desc}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Testimonials Section */}
      {/* <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeIn} className="mb-16">
        <h2 className="text-2xl font-bold mb-6 text-blue-700 text-center">What Our Users Say</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t) => (
            <div key={t.name} className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center">
              <div className="text-blue-600 text-3xl mb-2">“</div>
              <p className="text-gray-700 italic mb-4">{t.quote}</p>
              <div className="font-semibold text-blue-800">{t.name}</div>
              <div className="text-gray-500 text-sm">{t.title}</div>
            </div>
          ))}
        </div>
      </motion.div> */}

      {/* Call to Action Section (Now at End) */}
      {/* <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} variants={fadeIn} className="text-center mb-16">
        <h2 className="text-2xl font-bold mb-4 text-blue-800">Ready to see RoomRounds in action?</h2>
        <p className="mb-6 text-gray-700">Start your free trial or schedule a personalized demo with our team today.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="/pricing" className="bg-blue-600 text-white px-8 py-3 rounded-lg shadow hover:bg-blue-700 transition font-semibold">Start Free Trial</a>
          <a href="/contact" className="bg-white/90 border border-blue-600 text-blue-700 px-8 py-3 rounded-lg shadow hover:bg-blue-50 transition font-semibold">Schedule Demo</a>
        </div>
      </motion.div> */}

      {/* Fancy Gallery Section (Full Featured) */}
      {/* <GallerySection /> */}

      {/* <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={fadeIn}
        className="text-center mb-16"
      >
        <div className="bg-white rounded-2xl shadow p-6 flex flex-col items-center text-center">
          <video src={videoFile} width="100%" height="680" controls />
        </div>
      </motion.div> */}

      {/* Feature Image Dialog */}
      {selectedFeature !== null && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          onClick={() => setSelectedFeature(null)}
        >
          <motion.div
            className="bg-white rounded-2xl max-w-[1200px] w-full max-h-[90vh] overflow-auto mx-4"
            initial={{ scale: 0.8, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ duration: 0.6, type: "spring" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-blue-800">
                  {features[selectedFeature].title}
                </h3>
                <button
                  onClick={() => setSelectedFeature(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <img
                src={features[selectedFeature].src}
                alt={features[selectedFeature].title}
                className="w-full h-auto rounded-lg shadow-lg mb-4"
              />
              <p className="text-gray-600">
                {features[selectedFeature].description}
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </section>
  );
};

export default Features;
